// src/components/CertificatePage.js
import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { Container, Table, Button, Alert, Badge, Card } from 'react-bootstrap';
import logo from '../assets/logo.png'; // ✅ same as Login.js
import './CertificatePage.css';
import { formatVendorId } from '../utils/idFormat';

const CertificatePage = () => {
  const [vendors, setVendors] = useState([]);
  const [message, setMessage] = useState('');

  const fetchEligibleVendors = async () => {
    try {
      const res = await API.get('/admin/certificate-eligibility');
      setVendors(res.data.vendors || []);
    } catch (err) {
      setMessage('Failed to fetch eligible vendors');
    }
  };

  useEffect(() => {
    fetchEligibleVendors();
  }, []);

  const getInitials = (name = '') =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0].toUpperCase())
      .join('');

  const getStatus = (status) => {
    if (status === 'generated') return { label: 'Verified', tone: 'success' };
    if (status === 'rejected') return { label: 'Rejected', tone: 'danger' };
    return { label: 'Pending', tone: 'warning' };
  };

  const buildCertificateHtml = (vendor) => `
        <html lang="en">
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <title>Vendor Authorization Certificate</title>
            <link href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Noto+Sans:wght@400;500;700&display=swap" rel="stylesheet" />
            <style>
              :root {
                --primary: #17cf36;
                --bg: #f6f8f6;
                --parchment: #fdfcf8;
                --border-green: #0e6b1d;
                --text: #111812;
                --muted: #6b7280;
              }
              * { box-sizing: border-box; }
              body {
                margin: 0;
                background: var(--bg);
                font-family: "Newsreader", serif;
                color: var(--text);
              }
              .no-print { display: flex; }
              .page {
                min-height: 100vh;
                display: flex;
                flex-direction: column;
              }
              header {
                padding: 16px 32px;
                background: #fff;
                border-bottom: 1px solid #f0f4f1;
                display: flex;
                justify-content: space-between;
                align-items: center;
                position: sticky;
                top: 0;
              }
              header h2 { margin: 0; font-family: "Noto Sans", sans-serif; font-size: 16px; }
              main {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 40px 16px 64px;
              }
              .print-area {
                width: 100%;
                max-width: 900px;
                background: var(--parchment);
                position: relative;
                padding: 48px;
                border: 12px solid var(--border-green);
                box-shadow: 0 30px 60px rgba(0,0,0,0.15);
              }
              .watermark {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0.03;
                pointer-events: none;
              }
              .content {
                position: relative;
                z-index: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                gap: 20px;
              }
              .brand {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
              }
              .brand-row {
                display: flex;
                align-items: center;
                gap: 10px;
                font-family: "Noto Sans", sans-serif;
                font-weight: 700;
                letter-spacing: 0.2em;
                color: var(--border-green);
                text-transform: uppercase;
                font-size: 14px;
              }
              .brand-row img { height: 48px; }
              .subtitle {
                font-family: "Noto Sans", sans-serif;
                font-size: 10px;
                letter-spacing: 0.2em;
                color: #6b7280;
                text-transform: uppercase;
              }
              .title {
                font-size: 46px;
                color: var(--border-green);
                font-style: italic;
                margin: 10px 0 4px;
              }
              .divider {
                width: 120px;
                height: 4px;
                background: rgba(23, 207, 54, 0.2);
                margin: 6px auto 0;
              }
              .recipient p {
                font-family: "Noto Sans", sans-serif;
                color: #6b7280;
                margin: 0 0 10px;
              }
              .recipient h2 {
                margin: 0 0 8px;
                font-size: 32px;
                font-family: "Noto Sans", sans-serif;
                text-decoration: underline;
                text-decoration-color: rgba(23, 207, 54, 0.2);
                text-underline-offset: 8px;
              }
              .recipient .desc {
                max-width: 640px;
                font-style: italic;
                color: #374151;
                line-height: 1.6;
              }
              .details {
                width: 100%;
                display: grid;
                grid-template-columns: repeat(4, minmax(0, 1fr));
                gap: 16px;
                border-top: 1px solid rgba(23, 207, 54, 0.15);
                border-bottom: 1px solid rgba(23, 207, 54, 0.15);
                padding: 18px 0;
              }
              .detail-label {
                font-family: "Noto Sans", sans-serif;
                font-size: 10px;
                color: var(--primary);
                letter-spacing: 0.2em;
                text-transform: uppercase;
                font-weight: 700;
              }
              .detail-value {
                font-family: "Noto Sans", sans-serif;
                font-size: 13px;
                font-weight: 700;
              }
              .footer {
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                margin-top: 26px;
              }
              .signature {
                text-align: right;
                font-family: "Newsreader", serif;
              }
              .signature .name {
                font-style: italic;
                font-size: 22px;
              }
              .signature .line {
                width: 180px;
                height: 1px;
                background: #9ca3af;
                margin: 6px 0 8px auto;
              }
              .signature .role {
                font-family: "Noto Sans", sans-serif;
                font-size: 10px;
                letter-spacing: 0.2em;
                text-transform: uppercase;
                color: var(--primary);
                font-weight: 700;
              }
              .corners span {
                position: absolute;
                width: 80px;
                height: 80px;
                border: 4px solid rgba(23, 207, 54, 0.2);
              }
              .corners .tl { top: 8px; left: 8px; border-right: 0; border-bottom: 0; }
              .corners .tr { top: 8px; right: 8px; border-left: 0; border-bottom: 0; }
              .corners .bl { bottom: 8px; left: 8px; border-right: 0; border-top: 0; }
              .corners .br { bottom: 8px; right: 8px; border-left: 0; border-top: 0; }
              @media print {
                header, .no-print { display: none !important; }
                body { background: #fff !important; }
                .print-area { box-shadow: none !important; border-width: 8px; }
              }
              @media (max-width: 720px) {
                .print-area { padding: 32px; }
                .details { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                .footer { flex-direction: column; gap: 18px; align-items: flex-start; }
                .signature { text-align: left; }
                .signature .line { margin-left: 0; }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <header class="no-print">
                <h2>Lotus Rice Mill Pvt. Ltd.</h2>
                <div class="no-print">
                  <button onclick="window.print()" style="margin-right:8px;padding:8px 12px;border:none;border-radius:8px;background:var(--primary);color:#fff;font-weight:700;cursor:pointer;">Print</button>
                  <button onclick="window.print()" style="padding:8px 12px;border:1px solid #e5e7eb;border-radius:8px;background:#fff;color:#111812;font-weight:700;cursor:pointer;">PDF</button>
                </div>
              </header>
              <main>
                <div class="print-area">
                  <div class="watermark">
                    <svg width="420" height="420" viewBox="0 0 48 48" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.0799 24L4 19.2479L9.95537 8.75216L18.04 13.4961L18.0446 4H29.9554L29.96 13.4961L38.0446 8.75216L44 19.2479L35.92 24L44 28.7521L38.0446 39.2479L29.96 34.5039L29.9554 44H18.0446L18.04 34.5039L9.95537 39.2479L4 28.7521L12.0799 24Z"></path>
                    </svg>
                  </div>
                  <div class="content">
                    <div class="brand">
                      <div class="brand-row">
                        <img src="${logo}" alt="Lotus Rice Mill Pvt. Ltd." />
                        Lotus Rice Mill Pvt. Ltd.
                      </div>
                      <div class="subtitle">Excellence in Agriculture Since 1985</div>
                    </div>
                    <div>
                      <div class="title">Certificate of Authorization</div>
                      <div class="divider"></div>
                    </div>
                    <div class="recipient">
                      <p>This is to officially certify that</p>
                      <h2>${vendor.fullName}</h2>
                      <p class="desc">
                        has been duly vetted and is hereby recognized as an authorized distribution partner for all luxury grain products under the Lotus Rice Mill Pvt. Ltd. banner.
                      </p>
                    </div>
                    <div class="details">
                      <div>
                        <div class="detail-label">Vendor ID</div>
                        <div class="detail-value">${formatVendorId(vendor, vendor)}</div>
                      </div>
                      <div>
                        <div class="detail-label">GSTIN</div>
                        <div class="detail-value">${vendor.gstin || 'N/A'}</div>
                      </div>
                      <div>
                        <div class="detail-label">Verification</div>
                        <div class="detail-value">${String(vendor.fullName || '').split(' ')[0].toUpperCase() || 'VENDOR'}-${String(vendor.mobile || '').replace(/\\D/g,'').slice(0,5) || '00000'}-AUTH</div>
                      </div>
                      <div>
                        <div class="detail-label">Contact Email</div>
                        <div class="detail-value">${vendor.email || 'N/A'}</div>
                      </div>
                    </div>
                    <div class="footer">
                      <div>
                        <div class="detail-label">Date of Issue</div>
                        <div class="detail-value">${new Date().toLocaleDateString()}</div>
                      </div>
                      <div class="signature">
                        <div class="name">Anil Saxena</div>
                        <div class="line"></div>
                        <div class="role">Authorized Signatory</div>
                      </div>
                    </div>
                    <div class="corners">
                      <span class="tl"></span>
                      <span class="tr"></span>
                      <span class="bl"></span>
                      <span class="br"></span>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </body>
        </html>
      `;

  const openCertificate = (vendor, autoPrint = false) => {
    const certificateWindow = window.open('', '_blank');
    if (!certificateWindow) return;
    certificateWindow.document.write(buildCertificateHtml(vendor));
    certificateWindow.document.close();
    if (autoPrint) {
      setTimeout(() => {
        certificateWindow.focus();
        certificateWindow.print();
      }, 300);
    }
  };

  const handleGenerate = async (vendor) => {
    try {
      await API.put(`/admin/vendors/${vendor._id}/generate-certificate`);
      fetchEligibleVendors();
      openCertificate(vendor, false);
    } catch (err) {
      setMessage('Failed to generate certificate');
    }
  };

  const handleView = (vendor) => openCertificate(vendor, false);
  const handleDownload = (vendor) => openCertificate(vendor, true);

  const handleReject = async (vendorId) => {
    try {
      await API.put(`/admin/vendors/${vendorId}/reject-certificate`);
      fetchEligibleVendors();
    } catch (err) {
      setMessage('Failed to reject certificate');
    }
  };

  return (
    <Container className="mt-4 certificate-page">
      <div className="certificate-header">
        <div>
          <h4 className="mb-1">Eligible Vendors for Certificate</h4>
          <p className="certificate-subtitle">Manage, verify, and issue authorization certificates.</p>
        </div>
        <div className="certificate-actions">
          <Button variant="outline-secondary" size="sm">Refresh Data</Button>
        </div>
      </div>
      {message && <Alert variant="danger">{message}</Alert>}

      {/* Desktop View */}
      <div className="d-none d-md-block">
        <div className="certificate-table-card">
          <div className="certificate-table-header">
            <h5>Eligible Vendors</h5>
            <div className="certificate-table-tools">
              <span className="material-symbols-outlined">filter_list</span>
              <span className="material-symbols-outlined">more_vert</span>
            </div>
          </div>
          <Table hover responsive className="certificate-table">
            <thead>
            <tr>
              <th>Vendor Name</th>
              <th>Contact Info</th>
              <th>Current Status</th>
              <th className="text-end">Actions</th>
            </tr>
            </thead>
            <tbody>
              {vendors.map((vendor) => {
                const status = getStatus(vendor.certificateStatus);
                return (
                  <tr key={vendor._id}>
                    <td>
                      <div className="certificate-vendor">
                        <div className="certificate-avatar">{getInitials(vendor.fullName)}</div>
                        <span className="certificate-name">{vendor.fullName}</span>
                      </div>
                    </td>
                    <td>
                      <div className="certificate-contact">
                        <span className="certificate-phone">{vendor.mobile}</span>
                        <span className="certificate-email">{vendor.email}</span>
                      </div>
                    </td>
                    <td>
                      <Badge className={`certificate-status ${status.tone}`}>
                        <span className="status-dot" />
                        {status.label}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <div className="certificate-row-actions">
                        <Button
                          size="sm"
                          className="certificate-btn success"
                          onClick={() => handleGenerate(vendor)}
                          disabled={vendor.certificateStatus === 'generated'}
                        >
                          Generate Certificate
                        </Button>
                        <Button
                          size="sm"
                          className="certificate-btn secondary"
                          onClick={() => handleView(vendor)}
                          disabled={vendor.certificateStatus !== 'generated'}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          className="certificate-btn dark"
                          onClick={() => handleDownload(vendor)}
                          disabled={vendor.certificateStatus !== 'generated'}
                        >
                          Download PDF
                        </Button>
                        <Button
                          size="sm"
                          className="certificate-btn danger"
                          onClick={() => handleReject(vendor._id)}
                          disabled={vendor.certificateStatus === 'rejected'}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Mobile View (Cards) */}
      <div className="d-md-none">
        {vendors.map((vendor) => (
          <Card key={vendor._id} className="mb-3 shadow-sm certificate-card">
            <Card.Body>
              <div className="certificate-vendor">
                <div className="certificate-avatar">{getInitials(vendor.fullName)}</div>
                <div>
                  <h5 className="mb-1">{vendor.fullName}</h5>
                  <p className="mb-1 text-muted">{vendor.mobile}</p>
                  <p className="mb-1">{vendor.email}</p>
                </div>
              </div>
              <Badge
                className={`certificate-status ${getStatus(vendor.certificateStatus).tone} mb-2`}
              >
                <span className="status-dot" />
                {getStatus(vendor.certificateStatus).label}
              </Badge>
              <div className="d-flex gap-2 mt-2">
                <Button
                  size="sm"
                  className="certificate-btn success"
                  onClick={() => handleGenerate(vendor)}
                  disabled={vendor.certificateStatus === 'generated'}
                >
                  Generate Certificate
                </Button>
                <Button
                  size="sm"
                  className="certificate-btn secondary"
                  onClick={() => handleView(vendor)}
                  disabled={vendor.certificateStatus !== 'generated'}
                >
                  View
                </Button>
                <Button
                  size="sm"
                  className="certificate-btn dark"
                  onClick={() => handleDownload(vendor)}
                  disabled={vendor.certificateStatus !== 'generated'}
                >
                  PDF
                </Button>
                <Button
                  size="sm"
                  className="certificate-btn danger"
                  onClick={() => handleReject(vendor._id)}
                  disabled={vendor.certificateStatus === 'rejected'}
                >
                  Reject
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    </Container>
  );
};

export default CertificatePage;
